import { Fragment } from 'react';
import { Typography } from 'antd';
import { OliCardTile } from './OliCardTile';
import { OliGroup } from '../../pages/KitStaging/boards/groupByOli';
import { KtStagingArea, useConfirmMoveMutation, useUndoMoveMutation, useMovePairMutation } from '../../redux/api/kit-staging';
import { showMoveError, showMoveSuccess } from './moveMessages';

interface Props {
	area: KtStagingArea;
	groups: OliGroup[];
	// All boards' groups — used to find cards pending into THIS area but
	// currently elsewhere (e.g. still on the cart). Each such card is
	// rendered inline on its destination shelf with a yellow background
	// and Confirm / Undo actions.
	allGroups?: OliGroup[];
	currentArea: string;
	/** Accepts ``null`` so tapping an already-selected card deselects it
	 *  (same UX as Return Kit / Dekit). */
	onSelect: (g: OliGroup | null) => void;
	selectedGroup?: OliGroup | null;
}

function groupOnShelf(g: OliGroup, shelfId: number): boolean {
	return g.kitCard?.current_shelf === shelfId || g.materialCard?.current_shelf === shelfId;
}

function isUnshelved(g: OliGroup, currentArea: string): boolean {
	const cards = [g.kitCard, g.materialCard].filter(Boolean) as NonNullable<typeof g.kitCard>[];
	return cards.some((c) => c.current_area === currentArea && c.current_shelf == null);
}

/** A kit+material pair both shelved in JSA and both flagged site-mismatch, with
 *  no move already in flight — eligible for a one-tap pair reroute. */
function isSiteMismatchPair(g: OliGroup): boolean {
	const k = g.kitCard;
	const m = g.materialCard;
	return !!(
		k && m &&
		k.current_area === 'jsa' && m.current_area === 'jsa' &&
		k.status === 'site_mismatch' && m.status === 'site_mismatch' &&
		k.pending_target_area == null && m.pending_target_area == null
	);
}

interface PendingPlacement {
	group: OliGroup;
	oli: string;
	targetShelfId: number | null;
	/** Both kit + material are pending into this area → render ONE card and
	 *  confirm/undo them together via move_pair. */
	pairPending: boolean;
	/** A lone pending card's id (no partner pending). null when pairPending. */
	singleCardId: number | null;
}

/** Find every OLI with a pending move TARGETING this area type (jsa/msa).
 *  A kit+material pair is collapsed into ONE entry so it renders as a single
 *  card on its shared destination shelf — the operator confirms BOTH there
 *  with one tap (move_pair), not on the source. */
function pendingIntoArea(allGroups: OliGroup[] | undefined, areaType: string): PendingPlacement[] {
	if (!allGroups) return [];
	const out: PendingPlacement[] = [];
	for (const g of allGroups) {
		const kit = g.kitCard;
		const mat = g.materialCard;
		const kitPending = kit?.pending_target_area === areaType;
		const matPending = mat?.pending_target_area === areaType;
		if (!kitPending && !matPending) continue;
		const pairPending = !!(kitPending && matPending);
		const shelfId = (kitPending ? kit!.pending_target_shelf : mat!.pending_target_shelf) ?? null;
		out.push({
			group: g,
			oli: g.oli,
			targetShelfId: shelfId,
			pairPending,
			singleCardId: pairPending ? null : kitPending ? kit!.id : mat!.id,
		});
	}
	return out;
}

export function ShelfBoardView({ area, groups, allGroups, currentArea, onSelect, selectedGroup }: Props) {
	const unshelved = groups.filter((g) => isUnshelved(g, currentArea));
	const [confirmMove] = useConfirmMoveMutation();
	const [undoMove] = useUndoMoveMutation();
	const [movePair] = useMovePairMutation();
	const pending = pendingIntoArea(allGroups, currentArea);
	// Click-toggles-selection: tapping an already-selected card deselects.
	const handleSelect = (g: OliGroup) => {
		onSelect(selectedGroup?.oli === g.oli ? null : g);
	};

	// Index by destination shelf so we can render the pending card on the
	// shelf it'll land on. Also index by oli so the shelf-grid skips the
	// card's regular rendering (the pending tile replaces it).
	const pendingByShelf = new Map<number, PendingPlacement[]>();
	const pendingOlis = new Set<string>();
	for (const p of pending) {
		pendingOlis.add(p.group.oli);
		if (p.targetShelfId != null) {
			const arr = pendingByShelf.get(p.targetShelfId) ?? [];
			arr.push(p);
			pendingByShelf.set(p.targetShelfId, arr);
		}
	}

	const onConfirmPending = async (cardId: number) => {
		try {
			await confirmMove({ id: cardId }).unwrap();
			showMoveSuccess('Placement confirmed.');
		} catch (e) {
			showMoveError(e, "Couldn't confirm placement.");
		}
	};
	const onUndoPending = async (cardId: number) => {
		try {
			await undoMove({ id: cardId }).unwrap();
			showMoveSuccess('Move undone.');
		} catch (e) {
			showMoveError(e, "Couldn't undo move.");
		}
	};
	const onConfirmPair = async (oli: string) => {
		try {
			await movePair({ order_line_item_id: oli, action: 'confirm' }).unwrap();
			showMoveSuccess('Pair placed in JSA.');
		} catch (e) {
			showMoveError(e, "Couldn't confirm the pair.");
		}
	};
	const onUndoPair = async (oli: string) => {
		try {
			await movePair({ order_line_item_id: oli, action: 'undo' }).unwrap();
			showMoveSuccess('Pair returned to cart.');
		} catch (e) {
			showMoveError(e, "Couldn't undo the pair.");
		}
	};
	const onReroutePair = async (oli: string) => {
		try {
			await movePair({ order_line_item_id: oli, action: 'initiate' }).unwrap();
			showMoveSuccess('Rerouting kit + material to a serving shelf — confirm placement.');
		} catch (e) {
			showMoveError(e, "Couldn't reroute the pair.");
		}
	};

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 12 }}>
			<div style={{ display: 'flex', gap: 12, overflowX: 'auto', alignItems: 'flex-start' }}>
				{area.racks.map((rack) => (
					<Fragment key={rack.id}>
						<div style={{ display: 'flex', flexDirection: 'column', minWidth: 320, maxWidth: 400 }}>
							<div
								style={{
									textAlign: 'center',
									fontWeight: 700,
									fontSize: 12,
									padding: '6px 8px',
									color: '#595959',
									background: '#e5e7eb',
									borderRadius: '6px 6px 0 0',
								}}
							>
								Rack {rack.label}
							</div>
							<div
								style={{
									background: '#f5f5f5',
									padding: 6,
									borderRadius: '0 0 6px 6px',
									display: 'flex',
									flexDirection: 'column',
									gap: 10,
								}}
							>
								{rack.shelves.map((shelf) => {
									// Cards already on this shelf — skip ones whose group is
									// pending into this area (those render as the pending
									// tile so the operator doesn't see duplicates).
									const groupsHere = groups
										.filter((g) => groupOnShelf(g, shelf.id))
										.filter((g) => !pendingOlis.has(g.oli));
									const pendingHere = pendingByShelf.get(shelf.id) ?? [];
									const renderedAny = groupsHere.length + pendingHere.length > 0;
									return (
										<div key={shelf.id} style={{ flexShrink: 0 }}>
											{!renderedAny ? (
												<div
													style={{
														height: 60,
														border: '1.5px dashed #d1d5db',
														borderRadius: 6,
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														color: '#bfbfbf',
														fontSize: 11,
													}}
												>
													Shelf {shelf.position} — empty
												</div>
											) : (
												<>
													{groupsHere.map((g) => (
														<div key={g.oli} style={{ marginBottom: 4 }}>
															<OliCardTile
																group={g}
																currentArea={currentArea}
																selected={selectedGroup?.oli === g.oli}
																onSelect={handleSelect}
																pair={
																	isSiteMismatchPair(g)
																		? { mode: 'reroute', onReroute: () => onReroutePair(g.oli) }
																		: undefined
																}
															/>
														</div>
													))}
													{pendingHere.map((p) => (
												<div key={`pending-${p.oli}`} style={{ marginBottom: 4 }}>
													<OliCardTile
														group={p.group}
														currentArea={currentArea}
														selected={selectedGroup?.oli === p.group.oli}
														onSelect={handleSelect}
														pendingForCardId={p.pairPending ? null : p.singleCardId}
														onConfirmPending={onConfirmPending}
														onUndoPending={onUndoPending}
														pair={
															p.pairPending
																? { mode: 'confirm', onConfirm: () => onConfirmPair(p.oli), onUndo: () => onUndoPair(p.oli) }
																: undefined
														}
													/>
												</div>
											))}
												</>
											)}
										</div>
									);
								})}
							</div>
						</div>
						{rack.gap_after && <div style={{ width: 24, flexShrink: 0 }} />}
					</Fragment>
				))}
			</div>

			{unshelved.length > 0 && (
				<div>
					<Typography.Text strong style={{ fontSize: 12 }}>
						Unshelved ({unshelved.length})
					</Typography.Text>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
							gap: 8,
							marginTop: 6,
						}}
					>
						{unshelved.map((g) => (
							<div key={g.oli} style={{ flexShrink: 0 }}>
								<OliCardTile
									group={g}
									currentArea={currentArea}
									selected={selectedGroup?.oli === g.oli}
									onSelect={handleSelect}
								/>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
